// Shifts, cash and the day close (spec/13 §10, F-OPS-01 … F-OPS-07), redesigned for everyday use (2026-10-08).
//
//   Now        the open shift is the hero: its counter, who is on it, since when (a live duration), and its cash at a
//              glance — opening float, cash received, change given, cash in and out, and the expected cash, ticking as
//              sales land — with card and UPI totals and the sales count beside it. Its actions are the three a cashier
//              needs: Count & close, Cash in / out (a payout, a float top-up, a drop to the safe) and Hand over. With
//              several counters open, every counter shows as a card with its own figures and actions; a venue with one
//              counter sees only its shift. With none open, the empty state says what to do.
//   Movements  the open shift's cash timeline (./CashTimeline): every event on the drawer, with the running expected
//              cash, filtered by kind; a cash sale shows what was handed over and the change given back (ADR-0049).
//   Day close  the business-day review the manager closes on (./DayCloseReport, F-OPS-06), unchanged.
//   History    past shifts in a dense table — day, counter, staff, duration, expected, counted, the variance in its
//              tone, takings by tender — each opening its detail; Download CSV writes the table.
//
// Starting, counting and closing are centred dialogs (./ShiftFlows). The server owns the money: opening a float and
// every movement post through the ledger, a shift's expected drawer is read from it, and the close records the variance
// rather than adjusting a balance (spec/06 §2.4). `?shift=<shf_…>` opens one shift's detail (where a variance alert
// leads, F-OPS-07); `?day=` shows another day's report to sign off (ADR-0029). What each person may do is unchanged:
// `shifts:own` for their own drawer, `shifts:close_any` to end another's, `shifts:read_all` for every counter and the
// history, `shifts:cash_movements` for cash in and out, `shifts:day_close` for the day, `shifts:drawer_open` to kick it.

import type { CashMovement, CashMovementKind, DrawerCustody, Money, Shift } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Avatar, Badge, Button, Dialog, EmptyState, SegmentedControl, Skeleton, StatCard, Table, type TableColumn, TextField, useToast } from '@clavian/ui-web';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';

import { useAccess } from '../../api/queries';
import { problemCode, PROBLEM } from '../../api/problems';
import { Loading } from '../../shell/layouts';
import { LoadFailure } from '../../shell/LoadFailure';
import { Icon, NavIcon } from '../../shell/nav-icons';
import { useCurrentVenue } from '../../shell/scope';
import { Facts, Forbidden, Notice, Page, Section, SummaryStrip, TableSkeleton } from '../../ui';
import { TickingMoney } from '../../ui/ticking';
import { saveFile } from '../audit/save-file';
import { moneyFrom } from '../money';
import { useCommandStatus } from '../printing/command-status';
import { CashTimeline } from './CashTimeline';
import { DayCloseSection } from './DayCloseReport';
import { DayStatusStrip, localDateLabel } from './days-and-shifts';
import { RunningCounters } from './running-counters';
import { useBusinessDayStatus, useCurrentShift, useDayCloseReview, useOpenDrawer, useRecordCashMovement, useShift, useShiftMembership, useShifts } from './queries';
import { CountDrawerFlow, StartShiftFlow } from './ShiftFlows';
import { useRefusalText } from './refusal';
import { Variance } from './variance';

import './shifts.css';

const SHIFTS_OWN = 'shifts:own';
const SHIFTS_CASH_MOVEMENTS = 'shifts:cash_movements';
const SHIFTS_READ_ALL = 'shifts:read_all';
const SHIFTS_DAY_CLOSE = 'shifts:day_close';
const SHIFTS_CLOSE_ANY = 'shifts:close_any';
const SHIFTS_DRAWER_OPEN = 'shifts:drawer_open';
const REPORTS_FINANCIAL = 'reports:financial';
const ORDERS_READ = 'orders:read';

const MOVEMENT_KINDS: readonly CashMovementKind[] = ['pay_in', 'pay_out', 'drop'];

/** Cash put in or taken out by hand: a pay-in adds to the drawer; a payout and a drop take from it. */
export function netMovements(movements: readonly CashMovement[], currency: string): Money {
  const amount = movements.reduce((sum, movement) => sum + (movement.kind === 'pay_in' ? movement.amount.amount : -movement.amount.amount), 0);
  return { amount, currency };
}

/** How long a shift has run (or ran): "2 h 14 min", "45 min". Whole minutes, never negative. */
export function durationLabel(t: (key: string, options?: Record<string, unknown>) => string, fromIso: string, toMs: number): string {
  const minutes = Math.max(0, Math.floor((toMs - Date.parse(fromIso)) / 60_000));
  const hours = Math.floor(minutes / 60);
  if (hours >= 24) return t('shifts.now.daysHours', { days: Math.floor(hours / 24), hours: hours % 24 });
  return hours === 0 ? t('shifts.now.minutes', { minutes }) : t('shifts.now.hoursMinutes', { hours, minutes: minutes % 60 });
}

/** The time now, ticking each half minute, for a live duration. */
function useNow(intervalMs = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(timer);
  }, [intervalMs]);
  return now;
}

/** A tender's expected takings on a shift, from its X/Z report. */
function tenderExpected(shift: Shift, tender: 'cash' | 'counter_card' | 'counter_upi'): Money {
  return shift.tenders.find((count) => count.tender === tender)?.expected ?? { amount: 0, currency: shift.openingFloat.currency };
}

type FlowKind = 'start' | 'move' | 'handOver' | 'end' | 'drawer' | null;

export function ShiftsPage(): ReactNode {
  const { t } = useTranslation('console');
  const refusalText = useRefusalText();
  const format = useVenueFormatter();
  const venue = useCurrentVenue();
  const currency = venue.currency ?? 'INR';
  const access = useAccess({ venueId: venue.id });
  const ready = access.status === 'ready';
  const canOwn = ready && access.can(SHIFTS_OWN);
  const canMove = ready && access.can(SHIFTS_CASH_MOVEMENTS);
  const canReadAll = ready && access.can(SHIFTS_READ_ALL);
  const canDayClose = ready && access.can(SHIFTS_DAY_CLOSE);
  const canCloseAny = ready && access.can(SHIFTS_CLOSE_ANY);
  // The day close's sessions and occupancy come from the Reports overview, shown only to a member who may read it.
  const canReadReports = ready && access.can(REPORTS_FINANCIAL);
  // Open drawer (F-OPS-09) is offered only to a member who holds it here; the server refuses anyone else.
  const canOpenDrawer = ready && access.can(SHIFTS_DRAWER_OPEN);
  // The timeline's order numbers open the order in Orders for a member who may read orders.
  const canOpenOrders = ready && access.can(ORDERS_READ);
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { readonly shift?: unknown; readonly day?: unknown };
  const detailId = typeof search.shift === 'string' ? search.shift : undefined;
  // `?day=` shows another business day's report — one that closed at its rollover, to sign off (ADR-0029).
  const reviewDay = typeof search.day === 'string' ? search.day : undefined;
  const showShift = (shiftId: string | undefined) =>
    void navigate({
      to: '/v/$venueSlug/shifts',
      params: { venueSlug: venue.slug },
      search: { ...(shiftId === undefined ? {} : { shift: shiftId }), ...(reviewDay === undefined ? {} : { day: reviewDay }) },
    });

  const current = useCurrentShift(venue.organizationId, venue.id, canOwn || canReadAll);
  const shifts = useShifts(venue.organizationId, venue.id, canReadAll);
  const review = useDayCloseReview(venue.organizationId, venue.id, canDayClose, reviewDay);
  const status = useBusinessDayStatus(venue.organizationId, venue.id);
  // `shifts.enabled` (ADR-0029): a venue that does not track drawers is never asked to start a shift.
  const tracked = status.data?.shiftsEnabled !== false;

  const [flow, setFlow] = useState<FlowKind>(null);
  // The shift being counted, held from the moment its dialog opens: once it closes the page has no open shift, and
  // the dialog still shows the count's result (and, at a hand-over, who holds the drawer now).
  const [counting, setCounting] = useState<{ readonly shiftId: string; readonly own: boolean; readonly holder: string } | null>(null);
  const membership = useShiftMembership(venue.organizationId, venue.id);
  const { show } = useToast();

  if (access.status === 'loading') return <Loading />;
  if (!canOwn && !canReadAll && !canDayClose) {
    return (
      <Page title={t('shifts.title')}>
        <Forbidden message={t('shifts.notPermitted')} />
      </Page>
    );
  }
  if (current.isLoading) return <Loading />;
  if (current.isError && problemCode(current.error) !== PROBLEM.forbidden) {
    return <LoadFailure error={current.error} onRetry={() => void current.refetch()} />;
  }

  const openShift = current.data ?? null;
  // ADR-0029: several drawers may be open (one per counter), or one shared by colleagues (`shifts.cashHandling`).
  const drawers = status.data?.drawers ?? [];
  const cashHandling = status.data?.cashHandling ?? 'per_drawer';
  const shared = cashHandling === 'shared_drawer';
  const isMine = (drawer: DrawerCustody) => drawer.members.some((member) => member.memberId === access.memberId) || (drawer.members.length === 0 && drawer.openedByMemberId === access.memberId);
  const namesOf = (drawer: DrawerCustody | undefined) =>
    drawer === undefined
      ? t('shifts.holderUnknown')
      : (drawer.members.length > 0 ? drawer.members.map((member) => member.name ?? t('shifts.holderUnknown')) : [drawer.openedByName ?? t('shifts.holderUnknown')]).join(', ');
  const openDrawer = openShift === null ? undefined : drawers.find((drawer) => drawer.shiftId === openShift.id);
  // `shifts:own` ends the caller's own shift — the drawer they are on; ending another member's needs `shifts:close_any`
  // (F-OPS-03). Taking it over by a hand-over needs `shifts:own` alone: the incoming member counts the drawer (F-OPS-05,
  // ADR-0029); at a shared drawer one joins it instead. The server checks again; this only decides what is offered.
  const ownsOpenShift =
    openShift !== null &&
    (openDrawer === undefined ? openShift.openedByMemberId !== null && openShift.openedByMemberId === access.memberId : isMine(openDrawer));
  const holdsDrawer = drawers.some(isMine) || ownsOpenShift;
  const canEndOpen = canOwn && (ownsOpenShift || canCloseAny);
  const canHandOver = canOwn && tracked && (ownsOpenShift || !shared);
  const canJoin = canOwn && shared && openShift !== null && !ownsOpenShift;
  const canLeave = canOwn && shared && ownsOpenShift && (openDrawer?.members.length ?? 0) > 1;
  const mayStart = canOwn && tracked && !holdsDrawer && (!shared || drawers.length === 0);
  const holderName = namesOf(openDrawer ?? status.data?.custody ?? undefined);
  const dayLabel = status.data === undefined ? '' : localDateLabel(status.data.businessDay, format.locale);
  const countDrawer = (kind: 'handOver' | 'end', target?: DrawerCustody) => {
    const shiftId = target?.shiftId ?? openShift?.id;
    if (shiftId === undefined) return;
    const own = target === undefined ? ownsOpenShift : isMine(target);
    setCounting({ shiftId, own, holder: target === undefined ? holderName : namesOf(target) });
    setFlow(kind);
  };
  const changeMembership = (action: 'join' | 'leave') => {
    if (openShift === null) return;
    membership.mutate(
      { shiftId: openShift.id, action },
      {
        onSuccess: () => show({ tone: 'ok', title: action === 'join' ? t('shifts.joined') : t('shifts.left') }),
        onError: (error) => show({ tone: 'danger', title: refusalText(error, t('shifts.error.move')) }),
      },
    );
  };
  const showDay = (day: string | undefined) => void navigate({ to: '/v/$venueSlug/shifts', params: { venueSlug: venue.slug }, search: day === undefined ? {} : { day } });
  // Several counters at once (ADR-0029): every counter is a card with its own figures and actions.
  const multiCounter = drawers.length > 1;

  return (
    <Page
      title={t('shifts.title')}
      description={t('shifts.intro')}
      actions={
        <>
          {canOpenDrawer ? (
            <Button variant="secondary" icon={<Icon name="cash" />} onClick={() => setFlow('drawer')}>
              {t('shifts.drawer.open')}
            </Button>
          ) : null}
          {/* With no shift open at a one-counter venue, the empty state below offers it instead. */}
          {mayStart && (openShift !== null || multiCounter) ? (
            <Button variant={openShift === null ? 'primary' : 'secondary'} onClick={() => setFlow('start')}>
              {openShift === null ? t('shifts.now.start') : t('shifts.now.startAnother')}
            </Button>
          ) : null}
        </>
      }
    >
      <DayStatusStrip venue={venue} />
      {!tracked ? (
        <Notice tone="info" role="note">
          {t('shifts.notTracked')}
        </Notice>
      ) : null}

      <Section title={t('shifts.now.title')}>
        {openShift === null ? (
          multiCounter ? (
            <Notice tone="info" role="note">
              {t('shifts.drawers.none')}
            </Notice>
          ) : tracked ? (
            <EmptyState
              icon={<NavIcon name="shifts" />}
              title={t('shifts.noShiftTitle')}
              description={canOwn ? t('shifts.noShiftBody') : t('shifts.noShiftReadOnly')}
              action={
                mayStart ? (
                  <Button variant="primary" onClick={() => setFlow('start')}>
                    {t('shifts.now.start')}
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <p className="console-subtle">{t('shifts.now.untracked')}</p>
          )
        ) : (
          <>
            {canJoin ? (
              <Notice tone="info">
                {t('shifts.joinHint', { names: holderName, verb: (openDrawer?.members.length ?? 1) > 1 ? t('shifts.joinVerbMany') : t('shifts.joinVerbOne') })}
              </Notice>
            ) : canOwn && !ownsOpenShift && !canCloseAny ? (
              <Notice tone="info">{t('shifts.takeOverHint', { name: holderName })}</Notice>
            ) : null}
            <ShiftNow
              shift={openShift}
              holderNames={openDrawer === undefined ? [holderName] : openDrawer.members.length > 0 ? openDrawer.members.map((member) => member.name ?? t('shifts.holderUnknown')) : [holderName]}
              actions={
                <>
                  {canEndOpen ? (
                    <Button variant="primary" icon={<Icon name="check" />} onClick={() => countDrawer('end')}>
                      {t('shifts.now.countAndClose')}
                    </Button>
                  ) : null}
                  {canMove ? (
                    <Button variant="secondary" icon={<Icon name="cash" />} onClick={() => setFlow('move')}>
                      {t('shifts.now.cashInOut')}
                    </Button>
                  ) : null}
                  {canHandOver ? (
                    <Button variant={canEndOpen ? 'secondary' : 'primary'} onClick={() => countDrawer('handOver')}>
                      {ownsOpenShift ? t('shifts.now.handOver') : t('shifts.now.takeOver')}
                    </Button>
                  ) : null}
                  {canJoin ? (
                    <Button variant="primary" onClick={() => changeMembership('join')} busy={membership.isPending}>
                      {t('shifts.join')}
                    </Button>
                  ) : null}
                  {canLeave ? (
                    <Button variant="ghost" onClick={() => changeMembership('leave')} busy={membership.isPending}>
                      {t('shifts.leave')}
                    </Button>
                  ) : null}
                </>
              }
            />
          </>
        )}
        {/* A venue with two or more counters sees them all at a glance; one with a single counter, only its shift. */}
        <RunningCounters
            venue={venue}
            inlineShiftId={openShift?.id}
            title={t('shifts.now.allCounters')}
            minRows={2}
            actions={(shiftId) => {
              const drawer = drawers.find((one) => one.shiftId === shiftId);
              if (drawer === undefined) return null;
              return (
                <>
                  {canOwn && tracked && !shared && !isMine(drawer) ? (
                    <Button variant="secondary" size="sm" onClick={() => countDrawer('handOver', drawer)}>
                      {t('shifts.drawers.takeOver')}
                    </Button>
                  ) : null}
                  {canOwn && (canCloseAny || isMine(drawer)) ? (
                    <Button variant="ghost" size="sm" onClick={() => countDrawer('end', drawer)}>
                      {t('shifts.now.countAndClose')}
                    </Button>
                  ) : null}
                </>
              );
            }}
          />
      </Section>

      {openShift === null ? null : (
        <Section title={t('shifts.movementsSection.title')} description={t('shifts.movementsSection.hint')}>
          <CashTimeline
            organizationId={venue.organizationId}
            venueId={venue.id}
            venueSlug={venue.slug}
            shiftId={openShift.id}
            expectedCash={openShift.expectedCash}
            closed={false}
            canOpenOrders={canOpenOrders}
          />
        </Section>
      )}

      {canDayClose && reviewDay !== undefined ? (
        <Notice
          tone="info"
          role="status"
          action={
            <Button variant="secondary" size="sm" onClick={() => showDay(undefined)}>
              {t('shifts.signOff.today')}
            </Button>
          }
        >
          {t('shifts.signOff.viewing', { day: localDateLabel(reviewDay, format.locale) })}
        </Notice>
      ) : null}
      {canDayClose ? (
        <DayCloseSection
          review={review.data}
          error={review.error}
          refetch={() => void review.refetch()}
          organizationId={venue.organizationId}
          venueId={venue.id}
          onOpenShift={canReadAll ? showShift : undefined}
          canReadReports={canReadReports}
        />
      ) : null}

      {canReadAll ? (
        <Section
          title={t('shifts.history.title')}
          description={t('shifts.history.hint')}
          body="rows"
          actions={
            shifts.data !== undefined && shifts.data.shifts.some((shift) => shift.status === 'closed') ? (
              <Button variant="secondary" size="sm" icon={<Icon name="download" />} onClick={() => downloadHistory(shifts.data.shifts.filter((shift) => shift.status === 'closed'), t, format)}>
                {t('shifts.history.download')}
              </Button>
            ) : undefined
          }
        >
          {shifts.data === undefined ? (
            shifts.isError ? (
              <LoadFailure error={shifts.error} onRetry={() => void shifts.refetch()} />
            ) : (
              <TableSkeleton rows={4} />
            )
          ) : (
            <HistoryTable shifts={shifts.data.shifts.filter((shift) => shift.status === 'closed')} onOpen={showShift} />
          )}
        </Section>
      ) : null}

      <Dialog open={flow === 'start'} onOpenChange={(open) => setFlow(open ? 'start' : null)} size="md" dirty="auto" title={t('shifts.flow.startTitle')} description={t('shifts.flow.startHint')}>
        {flow === 'start' ? (
          <StartShiftFlow currency={currency} organizationId={venue.organizationId} venueId={venue.id} dayLabel={dayLabel} counterRequired={drawers.length > 0} onClose={() => setFlow(null)} />
        ) : null}
      </Dialog>
      <Dialog open={flow === 'move'} onOpenChange={(open) => setFlow(open ? 'move' : null)} size="sm" dirty="auto" title={t('shifts.dialog.move')} description={t('shifts.dialog.moveHint')}>
        {flow === 'move' && openShift !== null ? <MoveCashForm currency={currency} shiftId={openShift.id} organizationId={venue.organizationId} venueId={venue.id} onClose={() => setFlow(null)} /> : null}
      </Dialog>
      <Dialog open={detailId !== undefined} onOpenChange={(open) => (open ? undefined : showShift(undefined))} size="xl" title={t('shifts.detail.title')}>
        {detailId !== undefined ? (
          <ShiftDetail organizationId={venue.organizationId} venueId={venue.id} venueSlug={venue.slug} shiftId={detailId} enabled={canReadAll} canOpenOrders={canOpenOrders} />
        ) : null}
      </Dialog>
      <Dialog open={flow === 'drawer'} onOpenChange={(open) => setFlow(open ? 'drawer' : null)} size="sm" title={t('shifts.drawer.title')} description={t('shifts.drawer.hint')}>
        {flow === 'drawer' && canOpenDrawer ? <OpenDrawerForm organizationId={venue.organizationId} venueId={venue.id} venueSlug={venue.slug} onClose={() => setFlow(null)} /> : null}
      </Dialog>
      <Dialog
        open={flow === 'handOver' || flow === 'end'}
        onOpenChange={(open) => (open ? undefined : setFlow(null))}
        size="lg"
        dirty="auto"
        title={flow === 'end' ? t('shifts.flow.endTitle') : counting?.own === true ? t('shifts.flow.handOverTitle') : t('shifts.flow.takeOverTitle')}
      >
        {(flow === 'handOver' || flow === 'end') && counting !== null ? (
          <CountDrawerFlow
            mode={flow}
            currency={currency}
            shiftId={counting.shiftId}
            ownShift={counting.own}
            holderName={counting.holder}
            organizationId={venue.organizationId}
            venueId={venue.id}
            blind={status.data?.blindClose !== false}
            noteOver={status.data?.varianceNoteAmount ?? { amount: 10_000, currency }}
            onClose={() => setFlow(null)}
          />
        ) : null}
      </Dialog>
    </Page>
  );
}

/**
 * The open shift, front and centre: its counter and who is on it, since when (a live duration), the drawer's cash in a
 * summary strip that ticks as sales land — the expected cash first, on the live rail — then card, UPI and the sales
 * count, and the shift's actions.
 */
function ShiftNow({ shift, holderNames, actions }: { readonly shift: Shift; readonly holderNames: readonly string[]; readonly actions: ReactNode }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const now = useNow();
  const money = (value: Money) => <TickingMoney className="console-num" value={value} format={(one) => format.money(one)} />;
  const moved = netMovements(shift.movements, shift.openingFloat.currency);
  return (
    <article className="console-shift-now console-rail" data-rail="live" aria-label={t('shifts.now.label', { counter: shift.counterName ?? t('shifts.noCounter') })}>
      <header className="console-shift-now__head">
        <div className="console-shift-now__who">
          <span className="console-shift-now__avatars" aria-hidden="true">
            {holderNames.map((name) => (
              <Avatar key={name} name={name} size="md" decorative />
            ))}
          </span>
          <div>
            <h3 className="console-shift-now__counter">
              {shift.counterName ?? t('shifts.noCounter')}
              <Badge tone="ok" dot>
                {t('shifts.status.open')}
              </Badge>
            </h3>
            <p className="console-subtle console-shift-now__since">
              {t('shifts.now.since', {
                names: holderNames.join(', '),
                time: format.time(shift.openedAt),
                duration: durationLabel(t, shift.openedAt, now),
              })}
              {' · '}
              {localDateLabel(shift.businessDay, format.locale)}
            </p>
          </div>
        </div>
        <div className="console-shift-now__actions">{actions}</div>
      </header>
      <SummaryStrip label={t('shifts.figuresLabel')}>
        <StatCard className="console-summary__cell--live" label={t('shifts.now.expected')} value={money(shift.expectedCash)} hint={t('shifts.now.expectedHint')} />
        <StatCard label={t('shifts.openingFloat')} value={money(shift.openingFloat)} />
        <StatCard label={t('shifts.now.cashReceived')} value={money(shift.cashReceived)} hint={t('shifts.now.salesCount', { count: shift.salesCount })} />
        <StatCard label={t('shifts.now.changeGiven')} value={money(shift.changeGiven)} />
        <StatCard label={t('shifts.now.cashInOut')} value={money(moved)} hint={t('shifts.movementCount', { count: shift.movements.length })} />
      </SummaryStrip>
      <dl className="console-shift-now__other">
        <div>
          <dt>{t('shifts.tender.counter_card')}</dt>
          <dd className="console-num">{format.money(tenderExpected(shift, 'counter_card'))}</dd>
        </div>
        <div>
          <dt>{t('shifts.tender.counter_upi')}</dt>
          <dd className="console-num">{format.money(tenderExpected(shift, 'counter_upi'))}</dd>
        </div>
        <div>
          <dt>{t('shifts.now.salesLabel')}</dt>
          <dd className="console-num">{shift.salesCount}</dd>
        </div>
      </dl>
    </article>
  );
}

/** The history as a CSV: what the table shows, a row per closed shift. */
function downloadHistory(rows: readonly Shift[], t: (key: string, options?: Record<string, unknown>) => string, format: ReturnType<typeof useVenueFormatter>): void {
  const cell = (value: string) => (/[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value);
  const header = [
    t('shifts.businessDay'),
    t('shifts.counter'),
    t('shifts.history.col.staff'),
    t('shifts.openedAt'),
    t('shifts.closedAt'),
    t('shifts.history.col.duration'),
    t('shifts.expected'),
    t('shifts.counted'),
    t('shifts.variance'),
    t('shifts.tender.cash'),
    t('shifts.now.changeGiven'),
    t('shifts.tender.counter_card'),
    t('shifts.tender.counter_upi'),
  ];
  const lines = rows.map((shift) =>
    [
      shift.businessDay,
      shift.counterName ?? t('shifts.noCounter'),
      shift.openedByName ?? '',
      format.dateTime(shift.openedAt),
      shift.closedAt === null ? '' : format.dateTime(shift.closedAt),
      shift.closedAt === null ? '' : durationLabel(t, shift.openedAt, Date.parse(shift.closedAt)),
      format.money(shift.expectedCash),
      shift.countedCash === null ? '' : format.money(shift.countedCash),
      shift.cashVariance === null ? '' : format.money(shift.cashVariance),
      format.money(shift.cashSales),
      format.money(shift.changeGiven),
      format.money(tenderExpected(shift, 'counter_card')),
      format.money(tenderExpected(shift, 'counter_upi')),
    ]
      .map(cell)
      .join(','),
  );
  saveFile('shifts.csv', [header.map(cell).join(','), ...lines].join('\r\n') + '\r\n', 'text/csv');
}

/** Past shifts, dense: the day, counter, staff, how long, expected against counted with the variance in its tone, and takings. */
function HistoryTable({ shifts, onOpen }: { readonly shifts: readonly Shift[]; readonly onOpen: (shiftId: string) => void }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const columns: readonly TableColumn<Shift>[] = [
    { key: 'day', header: t('shifts.history.col.day'), cell: (shift) => <span className="console-num">{format.businessDate(shift.businessDay)}</span> },
    {
      key: 'counter',
      header: t('shifts.counter'),
      cell: (shift) => (
        <button type="button" className="console-linkbutton" onClick={() => onOpen(shift.id)} aria-label={t('shifts.history.open', { counter: shift.counterName ?? t('shifts.noCounter'), day: format.businessDate(shift.businessDay) })}>
          {shift.counterName ?? t('shifts.noCounter')}
        </button>
      ),
    },
    { key: 'staff', header: t('shifts.history.col.staff'), cell: (shift) => shift.openedByName ?? <span className="console-subtle">{t('shifts.holderUnknown')}</span> },
    {
      key: 'duration',
      header: t('shifts.history.col.duration'),
      cell: (shift) => (
        <span className="console-num" title={`${format.dateTime(shift.openedAt)} – ${shift.closedAt === null ? '' : format.dateTime(shift.closedAt)}`}>
          {shift.closedAt === null ? '—' : durationLabel(t, shift.openedAt, Date.parse(shift.closedAt))}
        </span>
      ),
    },
    { key: 'expected', header: t('shifts.expected'), numeric: true, cell: (shift) => <span className="console-money">{format.money(shift.expectedCash)}</span> },
    { key: 'counted', header: t('shifts.counted'), numeric: true, cell: (shift) => <span className="console-money">{shift.countedCash === null ? '—' : format.money(shift.countedCash)}</span> },
    {
      key: 'variance',
      header: t('shifts.variance'),
      numeric: true,
      cell: (shift) =>
        shift.cashVariance === null ? (
          <span className="console-subtle">—</span>
        ) : (
          <span className="console-history__variance">
            <Variance value={shift.cashVariance} />
            {shift.varianceAlerted ? (
              <Badge tone="danger" dot>
                {t('shifts.review.alerted')}
              </Badge>
            ) : null}
          </span>
        ),
    },
    { key: 'cash', header: t('shifts.tender.cash'), numeric: true, cell: (shift) => <span className="console-money">{format.money(shift.cashSales)}</span> },
    { key: 'card', header: t('shifts.history.col.card'), numeric: true, cell: (shift) => <span className="console-money">{format.money(tenderExpected(shift, 'counter_card'))}</span> },
    { key: 'upi', header: t('shifts.history.col.upi'), numeric: true, cell: (shift) => <span className="console-money">{format.money(tenderExpected(shift, 'counter_upi'))}</span> },
  ];
  return (
    <Table
      caption={t('shifts.history.title')}
      captionHidden
      className="console-table--dense"
      columns={columns}
      rows={shifts}
      rowKey={(shift) => shift.id}
      rowTone={(shift) => (shift.varianceAlerted ? 'danger' : undefined)}
      empty={t('shifts.history.empty')}
    />
  );
}

/**
 * One shift's detail (F-OPS-04, -07): who opened and closed it and when, expected against counted per tender, the
 * cashier's note, and its cash timeline — every drawer event through to the count, with the running expected drawer.
 */
function ShiftDetail({
  organizationId,
  venueId,
  venueSlug,
  shiftId,
  enabled,
  canOpenOrders,
}: {
  readonly organizationId: string;
  readonly venueId: string;
  readonly venueSlug: string;
  readonly shiftId: string;
  readonly enabled: boolean;
  readonly canOpenOrders: boolean;
}): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const shift = useShift(organizationId, venueId, shiftId, enabled);
  if (!enabled) return <Forbidden message={t('shifts.notPermitted')} />;
  if (shift.data === undefined) {
    return shift.isError ? <LoadFailure error={shift.error} onRetry={() => void shift.refetch()} /> : <Skeleton shape="text" lines={4} />;
  }
  const row = shift.data;
  return (
    <div className="console-form">
      {row.varianceAlerted ? (
        <Notice tone="danger" title={t('shifts.detail.alerted')}>
          {row.cashVariance === null ? null : <Variance value={row.cashVariance} />}
        </Notice>
      ) : null}
      <SummaryStrip label={t('shifts.detail.figures')}>
        <StatCard label={t('shifts.expected')} value={<span className="console-num">{format.money(row.expectedCash)}</span>} />
        <StatCard label={t('shifts.counted')} value={<span className="console-num">{row.countedCash === null ? '—' : format.money(row.countedCash)}</span>} />
        <StatCard
          label={t('shifts.variance')}
          tone={row.cashVariance === null || row.cashVariance.amount === 0 ? 'default' : row.cashVariance.amount < 0 ? 'danger' : 'warn'}
          value={row.cashVariance === null ? '—' : <Variance value={row.cashVariance} />}
        />
        <StatCard label={t('shifts.now.cashReceived')} value={<span className="console-num">{format.money(row.cashReceived)}</span>} hint={t('shifts.now.salesCount', { count: row.salesCount })} />
        <StatCard label={t('shifts.now.changeGiven')} value={<span className="console-num">{format.money(row.changeGiven)}</span>} />
      </SummaryStrip>
      <Facts
        columns={3}
        items={[
          { key: 'counter', label: t('shifts.counter'), value: row.counterName ?? t('shifts.noCounter') },
          { key: 'day', label: t('shifts.businessDay'), value: format.businessDate(row.businessDay) },
          { key: 'duration', label: t('shifts.history.col.duration'), value: <span className="console-num">{row.closedAt === null ? '—' : durationLabel(t, row.openedAt, Date.parse(row.closedAt))}</span> },
          { key: 'opened', label: t('shifts.detail.openedByMember'), value: <span>{row.openedByName ?? t('shifts.holderUnknown')} · <span className="console-num">{format.dateTime(row.openedAt)}</span></span> },
          {
            key: 'closed',
            label: t('shifts.detail.closedBy'),
            value: row.closedAt === null ? '—' : <span>{row.closedByName ?? t('shifts.holderUnknown')} · <span className="console-num">{format.dateTime(row.closedAt)}</span></span>,
          },
          { key: 'float', label: t('shifts.openingFloat'), value: <span className="console-num">{format.money(row.openingFloat)}</span> },
        ]}
      />
      <h3 className="console-eyebrow">{t('shifts.detail.tenders')}</h3>
      <Table
        caption={t('shifts.detail.tenders')}
        captionHidden
        rows={row.tenders}
        rowKey={(count) => count.tender}
        columns={[
          { key: 'tender', header: t('shifts.tenderLabel'), cell: (count) => t(`shifts.tender.${count.tender}`) },
          { key: 'expected', header: t('shifts.expected'), numeric: true, cell: (count) => <span className="console-money">{format.money(count.expected)}</span> },
          { key: 'counted', header: t('shifts.counted'), numeric: true, cell: (count) => <span className="console-money">{count.counted === null ? '—' : format.money(count.counted)}</span> },
          { key: 'variance', header: t('shifts.variance'), numeric: true, cell: (count) => (count.variance === null ? <span className="console-subtle">—</span> : <Variance value={count.variance} />) },
        ]}
      />
      <h3 className="console-eyebrow">{t('shifts.detail.note')}</h3>
      <p className={row.closingNote === null ? 'console-subtle' : undefined}>{row.closingNote ?? t('shifts.detail.noNote')}</p>
      <h3 className="console-eyebrow">{t('shifts.movementsSection.title')}</h3>
      <CashTimeline
        organizationId={organizationId}
        venueId={venueId}
        venueSlug={venueSlug}
        shiftId={row.id}
        expectedCash={row.expectedCash}
        closed={row.status === 'closed'}
        canOpenOrders={canOpenOrders}
      />
    </div>
  );
}

// -------------------------------------------------------------------------------------------
// Dialogs
// -------------------------------------------------------------------------------------------

/** Cash in or out by hand: cash put in (a float top-up), a payout, or a drop to the safe — with the reason. */
function MoveCashForm({ currency, shiftId, organizationId, venueId, onClose }: { readonly currency: string; readonly shiftId: string; readonly organizationId: string; readonly venueId: string; readonly onClose: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const refusalText = useRefusalText();
  const { show } = useToast();
  const record = useRecordCashMovement(organizationId, venueId);
  const [kind, setKind] = useState<CashMovementKind>('pay_in');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const money = moneyFrom(amount, currency);
    if (money === null || money.amount <= 0) {
      setError(t('shifts.error.amount'));
      return;
    }
    if (reason.trim() === '') {
      setError(t('shifts.error.reason'));
      return;
    }
    setError(undefined);
    record.mutate(
      { shiftId, body: { kind, amount: money, reason: reason.trim() } },
      {
        onSuccess: () => {
          show({ tone: 'ok', title: t('shifts.moved', { kind: t(`shifts.movementKind.${kind}`) }) });
          onClose();
        },
        onError: (err) => setError(refusalText(err, t('shifts.error.move'))),
      },
    );
  }

  return (
    <form className="console-form" aria-label={t('shifts.dialog.move')} noValidate onSubmit={submit}>
      {error !== undefined ? (
        <Notice tone="danger" role="alert">
          {error}
        </Notice>
      ) : null}
      <SegmentedControl<CashMovementKind>
        label={t('shifts.field.kind')}
        value={kind}
        onValueChange={setKind}
        block
        options={MOVEMENT_KINDS.map((value) => ({ value, label: t(`shifts.movementKind.${value}`) }))}
      />
      <p className="console-subtle">{t(`shifts.movementHint.${kind}`)}</p>
      <TextField label={t('shifts.field.amount', { currency })} required numeric inputMode="decimal" value={amount} onChange={(event) => setAmount(event.target.value)} />
      <TextField label={t('shifts.field.reason')} required value={reason} onChange={(event) => setReason(event.target.value)} />
      <div className="console-form__actions console-form__actions--end">
        <Button type="button" variant="ghost" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" variant="primary" busy={record.isPending}>
          {t('shifts.submit.move')}
        </Button>
      </div>
    </form>
  );
}

/**
 * Open drawer (F-OPS-09): a no-sale kick, with an optional reason for the audit row. The kick goes to the desk
 * device whose printer has the cash drawer; the dialog then follows it on `command.updated` — opened, failed, or
 * not answered in time. With no drawer set up, it points to Settings → Printers and hardware.
 */
function OpenDrawerForm({
  organizationId,
  venueId,
  venueSlug,
  onClose,
}: {
  readonly organizationId: string;
  readonly venueId: string;
  readonly venueSlug: string;
  readonly onClose: () => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const refusalText = useRefusalText();
  const open = useOpenDrawer(organizationId, venueId);
  const [reason, setReason] = useState('');
  const [problem, setProblem] = useState<{ readonly noDrawer: true } | { readonly message: string } | null>(null);
  const commandId = open.data?.commandId ?? null;
  const status = useCommandStatus(venueId, commandId);

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setProblem(null);
    const trimmed = reason.trim();
    open.mutate(trimmed === '' ? {} : { reason: trimmed }, {
      onError: (error) => {
        const code = problemCode(error);
        setProblem(code === PROBLEM.conflict ? { noDrawer: true } : { message: refusalText(error, t('shifts.drawer.failed')) });
      },
    });
  }

  if (open.data !== undefined) {
    const outcome = status ?? open.data.status;
    const tone = outcome === 'acked' ? 'ok' : outcome === 'failed' || outcome === 'expired' ? 'danger' : 'info';
    return (
      <div className="console-form">
        <Notice tone={tone} role={tone === 'danger' ? 'alert' : 'status'}>
          {t(`shifts.drawer.status.${outcome}`)}
        </Notice>
        <div className="console-form__actions console-form__actions--end">
          <Button variant="primary" onClick={onClose}>
            {t('shifts.drawer.done')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form className="console-form" aria-label={t('shifts.drawer.title')} noValidate onSubmit={submit}>
      {problem !== null && 'noDrawer' in problem ? (
        <Notice
          tone="warn"
          role="alert"
          title={t('shifts.drawer.noDrawerTitle')}
          action={
            <Button variant="secondary" size="sm" asChild>
              <Link to="/v/$venueSlug/settings/$group" params={{ venueSlug, group: 'hardware' }} hash="panel.printers">
                {t('shifts.drawer.setUp')}
              </Link>
            </Button>
          }
        >
          {t('shifts.drawer.noDrawer')}
        </Notice>
      ) : problem !== null ? (
        <Notice tone="danger" role="alert">
          {problem.message}
        </Notice>
      ) : null}
      <TextField label={t('shifts.drawer.reason')} description={t('shifts.drawer.reasonHint')} maxLength={200} value={reason} onChange={(event) => setReason(event.target.value)} />
      <div className="console-form__actions console-form__actions--end">
        <Button variant="ghost" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" variant="primary" busy={open.isPending}>
          {t('shifts.drawer.submit')}
        </Button>
      </div>
    </form>
  );
}
